import React from 'react';
import {
  CContainer,
  CListGroup,
  CListGroupItem,
  CCard,
  CCardBody,
  CCardText,
  CFormSelect,
  CForm,
  CFormLabel,
  CFormInput,
  CFormCheck,
  CCardHeader,
  CSpinner,
  CLoadingButton,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilCheckCircle } from '@coreui/icons';
import {
  initCarbonScoringInterface,
  userSelectedCropYear,
  userSelectedFarmField,
  fetchFarmFields,
  submitCarbonScoring,
  fetchCarbonScoringAnswers,
} from './carbon-scoring-actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import CarbonScoringFieldMap from './CarbonScoringFieldMap';

const CarbonScoring = () => {

  const dispatch = useDispatch();

  const {
    sidebarVisible,
    sidebarUnfoldable,
  } = useSelector(state => state.userSettings);

  const {
    selectedCropYear,
    cropYearsDropDownListDataSource,
    fetchingFarmFields,
    farmFields,
    selectedFarmField,
    carbonScoringAnswers,
    savingAnswers,
  } = useSelector(state => state.carbonScoring);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  const [formData, setFormData] = React.useState({});

  const currentYear = parseInt(selectedCropYear || '0');

  const formQuestions = [
    {
      id: 'crop',
      sortOrder: 1,
      type: 'radio',
      label: `What crop was grown on this field in ${selectedCropYear}?`,
      hint: 'Only corn and soy are qualified',
      options: [
        { value: 'corn', label: 'Corn' },
        { value: 'soy', label: 'Soy' }
      ],
      required: true
    },
    {
      id: 'coverCropEstablishment',
      sortOrder: 2,
      type: 'radio',
      label: `Cover Crop Establishment (Winter ${selectedCropYear}). This is a proven, high-impact climate-smart practice for reducing CI. If you claim it, you may be asked to provide a geo-tagged photo 2+ weeks after seeding, seed receipts, and commit to spring termination by winter-kill, herbicide, or non-soil-disturbing mechanical methods (roller-crimper or mowing). Are you interested in claiming this practice?`,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      required: true
    },
    {
      id: 'plantingCoverCrop',
      sortOrder: 3,
      type: 'radio',
      label: `Have you or will you be planting a cover crop on this field after harvest of your ${selectedCropYear} crop?`,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      relevant: { field: 'coverCropEstablishment', value: 'yes' },
      required: true
    },
    {
      id: 'terminationMethod',
      sortOrder: 4,
      type: 'radio',
      label: `What method will you be using to terminate the cover crop in spring of ${currentYear + 1}?`,
      options: [
        { value: 'winter_kill', label: 'Winter-kill' },
        { value: 'herbicide', label: 'Herbicide' },
        { value: 'roller_crimper_or_mowing', label: 'Roller-crimper or mowing' },
        { value: 'other_non_soil_disturbing_mech', label: 'Other non-soil disturbing mechanical operation' },
        { value: 'other', label: 'Other' }
      ],
      relevant: { field: 'plantingCoverCrop', value: 'yes' },
      required: true
    },
    {
      id: 'otherMethod',
      sortOrder: 5,
      type: 'text',
      label: 'If other, what is the method?',
      hint: 'Soil disturbing operations like full field tillage or burning, may disqualify this practice',
      relevant: { field: 'terminationMethod', values: ['other', 'other_non_soil_disturbing_mech'] },
      required: true
    },
    {
      id: 'agreeGeoTagged',
      sortOrder: 6,
      type: 'radio',
      label: 'Do you agree to take a geo-tagged photo of the cover crop 2+ weeks after planting?',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      relevant: { field: 'plantingCoverCrop', value: 'yes' },
      required: true
    },
    {
      id: 'coverCropAttestation',
      sortOrder: 7,
      type: 'text',
      label: `Cover Crop Attestation: I will plant a fall-seeded cover crop on this field after corn/soy harvest (interseeding allowed), will not harvest, graze, or fertilize it, will terminate in spring by winter-kill, herbicide, or non-soil-disturbing mechanical method (e.g., roller-crimper or mowing), and will upload one geo-tagged photo showing establishment 2+ weeks after seeding and provide seed receipts if requested.`,
      hint: 'Initial Here',
      relevant: { field: 'agreeGeoTagged', value: 'yes' },
      required: true
    },
    {
      id: 'tillageMethod',
      sortOrder: 8,
      type: 'radio',
      label: `Tillage Method (${selectedCropYear} Growing Season). Reduced till and no-till are CSA-eligible practices that can lower your field's CI. If you claim one, you may be asked for some form of operation or machine logs showing the implement used, tillage date, and depth, speed, or working width. Would you like to move forward with claiming reduced or no-till for this field?`,
      options: [
        { value: 'option_1', label: 'Yes' },
        { value: 'option_2', label: 'No' }
      ],
      required: true
    },
    {
      id: 'tillageType',
      sortOrder: 9,
      type: 'radio',
      label: 'What method of tillage was utilized on this field',
      options: [
        { value: 'conventional_tillage__whole_field_worked', label: 'Conventional tillage: Whole field worked with 1+ tillage passes, significant amount of soil exposed for planting (chisel, disk, field cultivator, vertical-till' },
        { value: 'reduced_tillage__whole_field_worked_with', label: 'Reduced tillage: Whole field worked with fewer and lighter passes so some crop residue remains on surface. No moldboard plow; no residue burning' },
        { value: 'no_till__no_entire_field_tillage_between', label: 'No-till: No entire field tillage between previous crop harvest and corn/soy planting. Seed placed in strip. Strip-till and fertilizer injection allowed. Previous crop residue remains; no residue burning' }
      ],
      relevant: { field: 'tillageMethod', value: 'option_1' },
      required: true
    },
    {
      id: 'noTillAttestation',
      sortOrder: 10,
      type: 'text',
      label: 'No-Till Attestation: There was no whole-field tillage between last harvest and this planting; only narrow-band/row-zone operations (e.g., strip-till or fertilizer injection) if any, no residue burning, and I can provide basic operation/machine logs if requested. If I claim no-till, I will continue no-till at least 4 of every 5 years.',
      hint: 'Initial Here',
      relevant: { field: 'tillageType', value: 'no_till__no_entire_field_tillage_between' },
      required: true
    },
    {
      id: 'reducedTillAttestation',
      sortOrder: 11,
      type: 'text',
      label: 'Reduced Till Attestation:This field used whole-field tillage with fewer/lighter passes, leaving noticeable residue on the surface; no moldboard plow and no residue burning were used, and I can provide basic operation/machine logs if requested',
      hint: 'Initial Here',
      relevant: { field: 'tillageType', value: 'reduced_tillage__whole_field_worked_with' },
      required: true
    },
    {
      id: 'optimizedNutrient',
      sortOrder: 12,
      type: 'radio',
      label: `Optimized Nutrient Management (${selectedCropYear} Growing Season). Claiming spring only (no fall N) or split in-season (≥75% after emergence) or use of an AAPFCO-defined nitrification inhibitor can reduce CI. If claimed, you may be asked for: a nutrient management plan with a soil test ≤ 2 years, and application records showing when and how much was applied (dates, method, amounts). Would you like to claim optimized nutrient management for this field?`,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      required: true
    },
    {
      id: 'fallNPercent',
      sortOrder: 13,
      type: 'number',
      label: `(Fall ${currentYear - 1}) What percent of N fertilizer was applied to this field after previous crop's harvest?`,
      hint: 'Input number 0 to 100',
      relevant: { field: 'optimizedNutrient', value: 'yes' },
      min: 0,
      max: 100,
      required: true
    },
    {
      id: 'springNPercent',
      sortOrder: 14,
      type: 'number',
      label: `(Spring ${selectedCropYear}) What percent of N fertilizer was applied to this field <30 days before or during planting?`,
      hint: 'Input number 0 to 100',
      relevant: { field: 'optimizedNutrient', value: 'yes' },
      min: 0,
      max: 100,
      required: true
    },
    {
      id: 'inSeasonNPercent',
      sortOrder: 15,
      type: 'number',
      label: `(In-season ${selectedCropYear}) What percent of N fertilizer was applied to this field after planting?`,
      hint: 'Input number 0 to 100',
      relevant: { field: 'optimizedNutrient', value: 'yes' },
      min: 0,
      max: 100,
      required: true
    },
    {
      id: 'nitrificationInhibitor',
      sortOrder: 16,
      type: 'radio',
      label: '(Nitrification Inhibitor) Did you use an AAPFCO-defined nitrification inhibitor that is accepted by the state with synthetic N applications on this field (including pre-plant), and will supply product labels/receipts if requested.',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      relevant: { field: 'optimizedNutrient', value: 'yes' },
      required: true
    },
    {
      id: 'optimizedNutrientAttestation',
      sortOrder: 17,
      type: 'text',
      label: 'Optimized Nutrient Management Attestation:I attest that the timing and percentage allocations of synthetic nitrogen I entered for this field—Fall, Spring (≤30 days before or at planting), and In-season (after emergence)—are true, complete, and cover all synthetic N applied to this field for this crop, and that the percentages total 100%.',
      hint: 'Initials Here',
      relevant: (formData) => formData.optimizedNutrient === 'yes' && formData.nitrificationInhibitor !== '' && formData.fallNPercent !== '' && formData.springNPercent !== '' && formData.inSeasonNPercent !== '',
      required: true
    },
    {
      id: 'certificationSignature',
      sortOrder: 18,
      type: 'text',
      label: `I certify that my per-field selections are accurate. I have operational control and decision authority over the fields where CSA practices are claimed. I will maintain or provide access to supporting records for five (5) years and authorize the use of remote sensing and, if requested, machine/application records to verify my claims. I will not double sell greenhouse gas benefits, and no land was converted into crop production after January 17, ${selectedCropYear}.`,
      hint: 'Signature here',
      required: true
    }
  ];

  React.useEffect(() => {
    dispatch(initCarbonScoringInterface());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize)
  }, []);

  React.useEffect(() => {
    if (selectedCropYear?.length > 0) {
      dispatch({ type: 'FETCHED_CARBON_SCORING_ANSWERS', answers: {} });
      setFormData({});
      dispatch(fetchFarmFields());
    }
  }, [selectedCropYear]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (selectedFarmField !== null) {
      dispatch(fetchCarbonScoringAnswers());
    }
    else {
      setFormData({});
    }
  }, [selectedFarmField]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    setFormData(carbonScoringAnswers || {});
  }, [carbonScoringAnswers]);

  const updateFormData = (key, value) => {
    console.log(`Updating form data: ${key} = ${value}`);
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    const missingFields = formQuestions
      .filter(question => question.required && isRelevant(question) && !formData[question.id])
      .map(question => question.label);
    if (missingFields.length > 0) {
      alert(`Please fill in the following required fields:\n${missingFields.join('\n')}`);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    dispatch(submitCarbonScoring(formData, formQuestions));
  };

  const isRelevant = (question) => {
    const relevant = question.relevant;
    if (!relevant) return true;
    if (typeof relevant === 'function') {
      return relevant(formData);
    }
    if (relevant.values) {
      return relevant.values.includes(formData[relevant.field]);
    }
    return formData[relevant.field] === relevant.value;
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  React.useEffect(() => {
    if (savingAnswers === false) {
      scrollToTop();
    }
  }, [savingAnswers]);

  const height = windowHeight - 64;

  var width = windowWidth;

  if (sidebarUnfoldable === true && sidebarVisible === true) {
    width -= 64;
  }
  else if (sidebarUnfoldable === false && sidebarVisible === true) {
    width -= 256;
  }

  return (
    <div style={{ flexDirection: 'row', display: 'flex', }}>
      <div style={{
        width: 300,
        height: height,
      }}>
        <CContainer fluid>
          <CCard>
            <CCardBody>
              <CFormSelect
                floatingLabel='Select Crop Year'
                className='mb-3'
                style={{ width: '100%', lineHeight: 4, }}
                value={selectedCropYear}
                options={cropYearsDropDownListDataSource}
                onChange={(e) => dispatch(userSelectedCropYear(e.target.value))}
              />
              <CCardText>
                Click on a Field Name below to complete the Carbon Scoring Questionnaire for the selected field.
              </CCardText>
              {
                fetchingFarmFields === true ? (
                  <div><CSpinner /> Loading Fields...</div>
                ) : null
              }
              <CListGroup>
                {
                  farmFields?.map((field, index) => {
                    return (
                      <CListGroupItem
                        key={index}
                        active={selectedFarmField?.fieldGUID === field.fieldGUID}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '16px',
                          cursor: 'pointer',
                        }}
                        onClick={() => dispatch(userSelectedFarmField(field))}>
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                          }}>
                          <div className='text-truncate fw-medium'>
                            {field.farmName}
                          </div>
                        </div>
                        {
                          field.hasCarbonScoringAnswers === true ? (
                            <CIcon
                              icon={cilCheckCircle}
                              size='lg'
                              style={{ color: 'black' }}
                              title='Scored' />
                          ) : null
                        }
                      </CListGroupItem>
                    );
                  })
                }
              </CListGroup>
            </CCardBody>
          </CCard>
          <div style={{ height: '20px' }} />
        </CContainer>
      </div>
      <div style={{ flexDirection: 'column', display: 'flex', }}>
        <div
          style={{
            width: width - 300,
            height: height,
          }}>
          {
            selectedFarmField !== null ? (
              <>
              {
                (selectedFarmField.GoogleMapFieldPolygons?.length > 0 ||
                  (selectedFarmField.geoCodedLatitude && selectedFarmField.geoCodedLongitude)) ? (
                  <CCard className='mt-2'>
                    <CCardBody>
                      <CarbonScoringFieldMap selectedFarmField={selectedFarmField} />
                    </CCardBody>
                  </CCard>
                ) : null
              }
              <CCard className='mt-2'>
                <CCardHeader>
                  <h3>Carbon Scoring Questionnaire</h3>
                </CCardHeader>
                <CCardBody>
                  <CForm>
                    {formQuestions.map((question) => {
                      const isVisible = isRelevant(question);
                      const isInvalid = question.required && isVisible && !formData[question.id];
                      return (
                        <div key={question.id} className={!isVisible ? 'd-none' : ''}>
                          {question.type === 'text' && (
                            <div className='mb-3'>
                              <CFormLabel>{question.label} {question.required && <span className='text-danger'>*</span>}</CFormLabel>
                              <CFormInput
                                type='text'
                                id={question.id}
                                value={formData[question.id] || ''}
                                onChange={(e) => updateFormData(question.id, e.target.value)}
                                placeholder={question.hint}
                                maxLength={1000000}
                                invalid={isInvalid}
                                {...(question.min || question.max ? { min: question.min, max: question.max } : {})}
                              />
                              {question.hint && <CFormLabel className='form-text'>{question.hint}</CFormLabel>}
                              {isInvalid && <div className='invalid-feedback d-block'>This field is required.</div>}
                            </div>
                          )}
                          {question.type === 'number' && (
                            <div className='mb-3'>
                              <CFormLabel>{question.label} {question.required && <span className='text-danger'>*</span>}</CFormLabel>
                              <CFormInput
                                type='number'
                                id={question.id}
                                value={formData[question.id] || ''}
                                onChange={(e) => updateFormData(question.id, e.target.value)}
                                placeholder={question.hint}
                                min={question.min}
                                max={question.max}
                                invalid={isInvalid}
                              />
                              {question.hint && <CFormLabel className='form-text'>{question.hint}</CFormLabel>}
                              {isInvalid && <div className='invalid-feedback d-block'>This field is required.</div>}
                            </div>
                          )}
                          {question.type === 'radio' && (
                            <div className='mb-3'>
                              <CFormLabel>{question.label} {question.required && <span className='text-danger'>*</span>}</CFormLabel>
                              {question.hint && <CFormLabel className='form-text'>{question.hint}</CFormLabel>}
                              <div>
                                {question.options.map((opt) => (
                                  <div key={opt.value} className='form-check'>
                                    <CFormCheck
                                      id={`${question.id}_${opt.value}`}
                                      type='radio'
                                      value={opt.value}
                                      checked={formData[question.id] === opt.value}
                                      onChange={(e) => {console.log(e.target); updateFormData(question.id, e.target.value)}}
                                      label={opt.label}
                                      inline
                                    />
                                  </div>
                                ))}
                              </div>
                              {isInvalid && <div className='invalid-feedback d-block'>This field is required.</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <CLoadingButton
                      loading={savingAnswers}
                      color='primary'
                      onClick={handleSubmit}>
                      Submit
                    </CLoadingButton>
                  </CForm>
                </CCardBody>
              </CCard>
              </>
            ) : null
          }
        </div>
      </div>
    </div>
  );
}

export default CarbonScoring;
